import { useEffect, useState, createContext, useContext } from "react";
import apiClient from "@/lib/apiClient";

import { toast } from "sonner";
import { Building2, Loader2, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mediscan_current_establishment";

const EstablishmentContext = createContext(null);

export const useEstablishment = () => useContext(EstablishmentContext);

export const EstablishmentProvider = ({ children }) => {
  const [establishment, setEstablishment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await apiClient.get(`${API}/establishments/current`);
      if (data?.establishment) {
        setEstablishment(data.establishment);
        setNeedsOnboarding(false);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data.establishment));
        } catch {
          // ignore quota errors
        }
      } else {
        // Pas d'établissement → afficher l'onboarding
        const cached = (() => {
          try {
            return localStorage.getItem(STORAGE_KEY);
          } catch {
            return null;
          }
        })();
        if (!cached) setNeedsOnboarding(true);
      }
    } catch (e) {
      // En cas d'erreur réseau, on ne bloque pas l'app
      console.warn("Establishment fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <EstablishmentContext.Provider
      value={{ establishment, loading, refresh, needsOnboarding, setNeedsOnboarding }}
    >
      {children}
      {!loading && needsOnboarding && (
        <EstablishmentOnboardingModal
          onCreated={async () => {
            setNeedsOnboarding(false);
            await refresh();
          }}
        />
      )}
    </EstablishmentContext.Provider>
  );
};

const ESTABLISHMENT_TYPES = [
  { value: "hospital", label: "Hôpital / Centre Hospitalier" },
  { value: "ehpad", label: "EHPAD / USLD" },
  { value: "clinic", label: "Clinique / SSR" },
  { value: "pharmacy", label: "Pharmacie" },
  { value: "cabinet", label: "Cabinet médical" },
];

export const EstablishmentOnboardingModal = ({ onCreated, onClose, initialEstablishment }) => {
  const isEditMode = Boolean(initialEstablishment?.establishment_id);
  const [form, setForm] = useState({
    name: initialEstablishment?.name || "",
    type: initialEstablishment?.type || "hospital",
    finess: initialEstablishment?.finess || "",
    city: initialEstablishment?.city || "",
    postal_code: initialEstablishment?.postal_code || "",
    medical_director: initialEstablishment?.medical_director || "",
    geriatrician_referent: initialEstablishment?.geriatrician_referent || "",
    specialties: initialEstablishment?.specialties || ["Gériatrie"],
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Le nom de l'établissement est obligatoire");
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode) {
        await apiClient.put(
          `${API}/establishments/${initialEstablishment.establishment_id}`,
          form
        );
        toast.success(`Établissement « ${form.name} » mis à jour`);
      } else {
        await apiClient.post(`${API}/establishments`, form);
        toast.success(`Établissement « ${form.name} » créé`);
      }
      onCreated?.();
    } catch (err) {
      console.error(err);
      toast.error(
        isEditMode
          ? "Impossible de mettre à jour l'établissement"
          : "Impossible de créer l'établissement"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="establishment-onboarding-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {isEditMode ? "Modifier votre établissement" : "Configurer votre établissement"}
              </h2>
              <p className="text-xs text-slate-600">
                Ces informations apparaîtront sur les rapports validés et personnalisent les recommandations.
              </p>
            </div>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="close-onboarding-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="est-name" className="text-sm font-medium text-slate-700">
                Nom de l'établissement <span className="text-red-500">*</span>
              </Label>
              <Input
                id="est-name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ex : CHU Pitié-Salpêtrière, EHPAD Les Tilleuls…"
                className="mt-1"
                data-testid="onboarding-name-input"
                required
              />
            </div>

            <div>
              <Label htmlFor="est-type" className="text-sm font-medium text-slate-700">
                Type
              </Label>
              <select
                id="est-type"
                value={form.type}
                onChange={(e) => handleChange("type", e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="onboarding-type-select"
              >
                {ESTABLISHMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="est-finess" className="text-sm font-medium text-slate-700">
                N° FINESS (optionnel)
              </Label>
              <Input
                id="est-finess"
                value={form.finess}
                onChange={(e) => handleChange("finess", e.target.value)}
                placeholder="9 chiffres"
                className="mt-1"
                data-testid="onboarding-finess-input"
              />
            </div>

            <div>
              <Label htmlFor="est-city" className="text-sm font-medium text-slate-700">
                Ville
              </Label>
              <Input
                id="est-city"
                value={form.city}
                onChange={(e) => handleChange("city", e.target.value)}
                placeholder="Paris"
                className="mt-1"
                data-testid="onboarding-city-input"
              />
            </div>

            <div>
              <Label htmlFor="est-postal" className="text-sm font-medium text-slate-700">
                Code postal
              </Label>
              <Input
                id="est-postal"
                value={form.postal_code}
                onChange={(e) => handleChange("postal_code", e.target.value)}
                placeholder="75013"
                className="mt-1"
                data-testid="onboarding-postal-input"
              />
            </div>

            <div>
              <Label htmlFor="est-medic-dir" className="text-sm font-medium text-slate-700">
                Médecin coordonnateur / Chef de service
              </Label>
              <Input
                id="est-medic-dir"
                value={form.medical_director}
                onChange={(e) => handleChange("medical_director", e.target.value)}
                placeholder="Dr. ..."
                className="mt-1"
                data-testid="onboarding-medical-director-input"
              />
            </div>

            <div>
              <Label htmlFor="est-geriatrician" className="text-sm font-medium text-slate-700">
                Gériatre référent
              </Label>
              <Input
                id="est-geriatrician"
                value={form.geriatrician_referent}
                onChange={(e) => handleChange("geriatrician_referent", e.target.value)}
                placeholder="Dr. ..."
                className="mt-1"
                data-testid="onboarding-geriatrician-input"
              />
            </div>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row gap-2 justify-end border-t border-slate-100 mt-4">
            {onClose && (
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting}
                data-testid="onboarding-cancel-btn"
              >
                Plus tard
              </Button>
            )}
            <Button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white"
              disabled={submitting}
              data-testid="onboarding-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEditMode ? "Enregistrement..." : "Création..."}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? "Enregistrer les modifications" : "Créer mon établissement"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EstablishmentOnboardingModal;
